import React, { FC, Fragment, useMemo, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { Alert, AlertTitle, Button } from '@mui/material';
import { customerDirectoryTemplate } from 'apollo/templates/customer-directory';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import AddWhiteLabelClinicDialog from 'components/client-directory/add-white-label-clinic-dialog/add-white-label-clinic-dialog.component';
import { DirectoryBrowserConfig } from 'components/supplier-browser/directory-browser-config';
import DirectoryBrowser from 'components/supplier-browser/directory-browser.component';
import { CONSTANTS } from 'config/constants';
import { customerSettings } from 'utils/customerSettings';
import { Box } from '@mui/material';
import OpeningHours from 'components/connect-to-lab/lab-details-dialog/opening-hours.component';
import { Hours } from 'models/organization';
import { renderCustomerAction } from './customer-action.component';

/** DD-3698: the pills — what kind of organization, in the Clients tab's terms. */
const CUSTOMER_TYPES = ['PRO', 'WHITE_LABEL', 'LAB'] as const;

const CUSTOMER_TYPE_LABEL_KEYS: Record<string, string> = {
    PRO: 'client-directory:DENTHUB_CONNECTED',
    WHITE_LABEL: 'client-directory:WHITE_LABEL',
    LAB: 'client-directory:LAB_CUSTOMER',
};

/**
 * DD-3706: action-based clinics (e.g. regional public dental care) work only with the labs
 * in their procurement; they become the lab's customer that way, never from here.
 */
const ProcurementInfo: FC<{ name: string }> = ({ name }) => {
    const { t: translate } = useTranslation(['client-directory']);
    return (
        <Alert severity="info" sx={{ mt: 2 }} data-test="customer-browser.procurement-info">
            <AlertTitle>{translate('client-directory:ACTION_BASED_CUSTOMER_TITLE')}</AlertTitle>
            {translate('client-directory:ACTION_BASED_CUSTOMER_INFO', { name })}
        </Alert>
    );
};

/**
 * DD-3698 "Find customer": labs browse the clinics and labs they do not work with yet, the
 * way they find suppliers — the same cards, filters, map and profile — with a country filter.
 * Labs and white-label clinics can be added as customers from here; an organization that is
 * not listed can be added the old way, with the Add customer dialog. Denthub Pro clinics get
 * requests later (DD-3702).
 */
const CustomerBrowser: FC = () => {
    const { t: translate } = useTranslation(['client-directory']);
    const client = useApolloClient();
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    // The same setting connectWhiteLabelClinicToLab enforces; without it Add would fail.
    const canAddWhiteLabel = Boolean(
        customerSettings.getCustomerSetting(currentOrgVar()?.customerSettings, CONSTANTS.SETTINGS_NAMES.WHITE_LABEL),
    );

    const config = useMemo<DirectoryBrowserConfig>(
        () => ({
            query: customerDirectoryTemplate,
            field: 'customerDirectory',
            services: CUSTOMER_TYPES,
            serviceLabelKey: (type) => CUSTOMER_TYPE_LABEL_KEYS[type] ?? type,
            countryFilter: true,
            // Only organizations the lab does not work with are listed, so nothing to hide.
            hideConnectedFilter: false,
            showOrganizationNumber: true,
            texts: {
                noneListed: 'client-directory:NO_CUSTOMERS_LISTED',
                noneMatch: 'client-directory:NO_CUSTOMERS_MATCH',
                mapTitle: 'client-directory:CUSTOMER_MAP_TITLE',
                mapOnlyLocated: 'client-directory:CUSTOMER_MAP_ONLY_LOCATED',
                servicesFilter: 'client-directory:CUSTOMER_TYPE_FILTER',
            },
            renderAction: (lab) => renderCustomerAction(lab, canAddWhiteLabel),
            // DD-3698: a lab's opening hours, as in Find lab (clinics have none here).
            // DD-3706: an action-based clinic explains why it cannot be added or asked.
            renderDetails: (lab) => {
                if (lab.isActionBased) return <ProcurementInfo name={lab.name ?? ''} />;
                const { openingHours } = lab as typeof lab & { openingHours?: Hours[] };
                return openingHours?.length ? (
                    <Box mt={2}>
                        <OpeningHours currentLab={{ openingHours }} />
                    </Box>
                ) : undefined;
            },
            renderNotice: () => (
                <Alert
                    severity="info"
                    sx={{ mb: 2 }}
                    action={
                        <Button
                            color="inherit"
                            size="small"
                            onClick={() => setIsAddDialogOpen(true)}
                            data-test="customer-browser.add-yourself"
                        >
                            {translate('client-directory:ADD_CUSTOMER_YOURSELF')}
                        </Button>
                    }
                    data-test="customer-browser.cant-find"
                >
                    {translate('client-directory:CANT_FIND_CUSTOMER')}
                </Alert>
            ),
        }),
        [canAddWhiteLabel, translate],
    );

    return (
        <Fragment>
            <DirectoryBrowser config={config} />
            <AddWhiteLabelClinicDialog
                dialogOpen={isAddDialogOpen}
                onCloseDialog={() => setIsAddDialogOpen(false)}
                onCustomerAdded={() => client.refetchQueries({ include: ['customerDirectory'] })}
            />
        </Fragment>
    );
};

export default CustomerBrowser;
